import { useTranslations } from '@vocab/react';
import {
  Box,
  Checkbox,
  Divider,
  IconNewWindow,
  Stack,
  TextLink,
} from 'braid-design-system';
import { type FormEvent, useEffect } from 'react';
import {
  type Control,
  Controller,
  type UseFormClearErrors,
  type UseFormSetValue,
} from 'react-hook-form';

import { Page, useTrackingClient } from 'src/context/tracking';
import {
  type PrivacyPolicy,
  usePrivacyPolicy,
} from 'src/pages/Review/RoleRequirementsSummary/hooks/usePrivacyPolicy/usePrivacyPolicy';

import translations from './PrivacyPolicy.vocab';

export interface PrivacyPolicyField {
  id: string;
  value: boolean;
}

interface PrivacyPolicyProps {
  pageViewedFired: boolean;
  privacyPolicy: PrivacyPolicy;
  privacyPolicyMessage?: string;
  setValue: UseFormSetValue<any>;
  value: PrivacyPolicyField;
}

const PrivacyPolicyComponent = ({
  privacyPolicy,
  privacyPolicyMessage,
  pageViewedFired,
  setValue,
  value,
}: PrivacyPolicyProps) => {
  const { t } = useTranslations(translations);
  const client = useTrackingClient();

  useEffect(() => {
    if (privacyPolicyMessage) {
      client.trackLink({
        link: 'candidate-did-not-agree-to-privacy-statement',
        pageTitle: Page.REVIEW_PAGE,
        currentPage: Page.REVIEW_PAGE,
        eventName: ['submit_error_viewed'],
      });
    }
  }, [privacyPolicyMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pageViewedFired) {
      client.trackLink({
        link: 'role_requirements_privacy_question_viewed',
        pageTitle: Page.REVIEW_PAGE,
        currentPage: Page.REVIEW_PAGE,
        eventName: ['role_requirements_privacy_question_viewed'],
      });
    }
  }, [pageViewedFired]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack space="small">
      <Checkbox
        id="privacyPolicy"
        label={
          <>
            {privacyPolicy?.text || ''}{' '}
            <Box component="span" position="relative" zIndex={1}>
              <TextLink
                weight="weak"
                hitArea="large"
                href={privacyPolicy?.url || '#'}
                onClick={() => {
                  client.trackLink({
                    link: 'privacy_statement_pressed',
                    pageTitle: Page.REVIEW_PAGE,
                    currentPage: Page.REVIEW_PAGE,
                    eventName: ['privacy_statement_pressed'],
                    isExitLink: true,
                  });
                }}
                target="_blank"
              >
                {t('Click to view')} <IconNewWindow />
              </TextLink>
            </Box>
          </>
        }
        onChange={(event: FormEvent<HTMLInputElement>) => {
          const target = event.currentTarget;
          setValue(
            'privacyPolicy',
            { id: value.id, value: target.checked },
            { shouldValidate: true },
          );
        }}
        checked={Boolean(value.value)}
        message={String(privacyPolicyMessage)}
        tone={privacyPolicyMessage ? 'critical' : 'neutral'}
      />
    </Stack>
  );
};

export const PrivacyPolicyInput = ({
  pageViewedFired,
  privacyPolicyMessage,
  control,
  clearErrors,
  setValue,
}: {
  pageViewedFired: boolean;
  privacyPolicyMessage?: string;
  control: Control<any>;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
}) => {
  const { t } = useTranslations(translations);
  const { data } = usePrivacyPolicy();

  if (!data) {
    return null;
  }

  return (
    <Controller
      control={control}
      render={({ field }) => (
        <Stack space="medium">
          <PrivacyPolicyComponent
            {...field}
            privacyPolicyMessage={String(privacyPolicyMessage)}
            privacyPolicy={data}
            pageViewedFired={pageViewedFired}
            setValue={setValue}
          />
          <Divider />
        </Stack>
      )}
      name="privacyPolicy"
      defaultValue={{ id: data.id, value: false }}
      rules={{
        validate: (privacyPolicyField: PrivacyPolicyField) => {
          const message = t('You must accept');
          const invalidField = !privacyPolicyField.value;

          if (invalidField) {
            control.setError('privacyPolicy', {
              message,
            });

            return message;
          }

          clearErrors('privacyPolicy');

          return;
        },
      }}
    />
  );
};
